// Artist directory + public profile + booking modal.
//
// The booking modal supports three flows in a single component:
//   1. Authed user             → POST /api/bookings
//   2. Guest user (no account) → POST /api/bookings/guest (one-step signup + book)
//   3. Returning visitor       → "Already a member? Sign in" link routes through
//                                /sign-in with state.from carrying the artist
//                                URL plus ?book=1 so this page knows to
//                                auto-reopen the modal after sign-in (see the
//                                second useEffect below).
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BookingBadge } from "../components/Badges";
import { Search, Instagram } from "lucide-react";
import { toast } from "sonner";
import UploadButton from "../components/UploadButton";
import Seo from "../components/Seo";

const STYLES = ["Traditional","Neo-traditional","Japanese","Blackwork","Watercolor","Geometric","Realism","Minimalist"];

export function ArtistsList() {
  const [artists, setArtists] = useState([]);
  const [q, setQ] = useState(""); const [style, setStyle] = useState("");
  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (style) params.style = style;
    api.get("/artists", { params }).then((r) => setArtists(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [style]);

  return (
    <div className="container-x py-12" data-testid="artists-page">
      <Seo title="Tattoo artists" description="Browse tattoo artists by style and location, view their portfolios and request a booking on Inkstead."/>
      <h1 className="font-serif text-4xl md:text-5xl">Artists</h1>
      <p className="text-black/60 mt-2">Find your next collaborator.</p>
      <div className="mt-8 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center border-[0.5px] border-black/20 rounded-[8px] px-3">
          <Search size={16} className="text-black/40"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter' && load()} placeholder="Search by name or location" className="flex-1 py-2 px-2 outline-none text-sm bg-transparent" data-testid="artists-search"/>
          <button onClick={load} className="text-sm underline underline-offset-4" data-testid="artists-search-btn">Search</button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={()=>setStyle("")} className={`badge ${style===""?"badge-flash":"badge-neutral"}`} data-testid="filter-all">All</button>
          {STYLES.map((s) => (
            <button key={s} onClick={()=>setStyle(s)} className={`badge ${style===s?"badge-flash":"badge-neutral"}`} data-testid={`filter-${s.toLowerCase()}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
        {artists.map((a) => (
          <Link key={a.id} to={`/artists/${a.username}`} className="card hover:-translate-y-0.5 transition-transform" data-testid={`artist-card-${a.username}`}>
            <div className="aspect-square overflow-hidden rounded-[8px] mb-4 bg-neutral-100">
              {a.avatar_url && <img src={a.avatar_url} alt={a.name} className="w-full h-full object-cover"/>}
            </div>
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl">{a.name}</h3>
              <BookingBadge status={a.booking_status}/>
            </div>
            <p className="text-sm text-black/60 mt-1">{a.location}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {(a.style_tags || []).slice(0,3).map((t) => <span key={t} className="badge badge-neutral">{t}</span>)}
            </div>
          </Link>
        ))}
        {artists.length === 0 && <p className="text-black/50">No artists yet.</p>}
      </div>
    </div>
  );
}

export function ArtistProfile() {
  const { username } = useParams();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [form, setForm] = useState({ preferred_date: "", body_placement: "", description: "", reference_image_url: "" });
  const [guest, setGuest] = useState({ name: "", email: "", password: "" });
  const [submitErr, setSubmitErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // tomorrow as the minimum bookable date (ISO YYYY-MM-DD)
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  useEffect(() => { api.get(`/artists/${username}`).then((r) => setData(r.data)).catch(() => setData("notfound")); }, [username]);

  // Auto-open the booking modal if we returned here via ?book=1 after sign-in
  useEffect(() => {
    if (user && searchParams.get("book") === "1" && data && data !== "notfound") {
      setShowBook(true);
      const next = new URLSearchParams(searchParams);
      next.delete("book");
      setSearchParams(next, { replace: true });
    }
  }, [user, data, searchParams, setSearchParams]);

  const openBooking = () => {
    setSubmitErr("");
    setShowBook(true);
  };

  const book = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    if (form.preferred_date && form.preferred_date < minDate) {
      setSubmitErr("Pick a date after today");
      return;
    }
    setSubmitting(true);
    try {
      if (user) {
        await api.post("/bookings", { artist_id: data.artist.id, ...form });
      } else {
        const r = await api.post("/bookings/guest", { artist_id: data.artist.id, ...form, ...guest });
        localStorage.setItem("inkstead_token", r.data.token);
        setUser(r.data.user);
      }
      toast.success("Booking request sent");
      setShowBook(false);
      setForm({ preferred_date: "", body_placement: "", description: "", reference_image_url: "" });
      setGuest({ name: "", email: "", password: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setSubmitErr("An account with that email already exists. Sign in to continue.");
      } else if (typeof detail === "string") {
        setSubmitErr(detail);
      } else {
        setSubmitErr("Could not send request");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (data === "notfound") return <div className="container-x py-20"><Seo title="Artist not found" noindex/>Artist not found.</div>;
  if (!data) return <div className="container-x py-20 text-black/50">Loading...</div>;
  const { artist, portfolio } = data;

  return (
    <div className="container-x py-12" data-testid="artist-profile">
      <Seo title={`${artist.name} – tattoo artist`} description={artist.bio ? artist.bio.slice(0, 160) : `View ${artist.name}'s tattoo portfolio and request a booking on Inkstead.`} image={artist.avatar_url} type="profile"/>
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="aspect-square overflow-hidden rounded-[12px] border-[0.5px] border-black/10 bg-neutral-100">
            {artist.avatar_url && <img src={artist.avatar_url} alt={artist.name} className="w-full h-full object-cover"/>}
          </div>
          <h1 className="font-serif text-3xl mt-6" data-testid="artist-name">{artist.name}</h1>
          <p className="text-black/60 mt-1">@{artist.username} · {artist.location}</p>
          <div className="mt-3"><BookingBadge status={artist.booking_status}/></div>
          <div className="flex flex-wrap gap-2 mt-4">
            {(artist.style_tags || []).map((t) => <span key={t} className="badge badge-neutral">{t}</span>)}
          </div>
          <p className="mt-4 text-black/80">{artist.bio}</p>
          {artist.instagram_handle && (
            <a
              href={`https://instagram.com/${artist.instagram_handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-black hover:underline underline-offset-4"
              data-testid="artist-instagram-link"
            >
              <Instagram size={16}/> @{artist.instagram_handle}
            </a>
          )}
          <button onClick={openBooking} className="btn-filled mt-6 w-full" data-testid="open-booking">Request booking</button>
        </div>
        <div className="md:col-span-2">
          <h2 className="font-serif text-2xl mb-6">Portfolio</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {portfolio.map((p) => (
              <div key={p.id} className="card !p-0 overflow-hidden" data-testid={`portfolio-${p.id}`}>
                <div className="aspect-square overflow-hidden"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover"/></div>
                <div className="p-3">
                  <div className="text-sm font-medium truncate">{p.title}</div>
                  <span className="badge badge-neutral mt-1">{p.style_tag}</span>
                </div>
              </div>
            ))}
            {portfolio.length === 0 && <p className="text-black/50 col-span-3">No portfolio pieces yet.</p>}
          </div>
        </div>
      </div>

      {showBook && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBook(false)}>
          <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-1">Request a session with {artist.name}</h3>
            {!user && (
              <p className="text-sm text-black/60 mb-4" data-testid="guest-mode-hint">
                No account needed. We'll create one for you so you can track this booking.
                {" "}<Link to="/sign-in" state={{ from: `${location.pathname}?book=1` }} className="underline" data-testid="guest-signin-link">Already a member? Sign in</Link>
              </p>
            )}
            <form onSubmit={book} className="space-y-4" data-testid="booking-form">
              {!user && (
                <div className="space-y-3 pb-4 border-b-[0.5px] border-black/10" data-testid="guest-fields">
                  <div>
                    <label className="text-sm font-medium block mb-1.5">Your name</label>
                    <input required value={guest.name} onChange={(e)=>setGuest({...guest, name: e.target.value})} placeholder="Sam Patel" className="input-field" data-testid="guest-name"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1.5">Email</label>
                    <input type="email" required value={guest.email} onChange={(e)=>setGuest({...guest, email: e.target.value})} placeholder="you@email.com" className="input-field" data-testid="guest-email"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1.5">Password</label>
                    <input type="password" required minLength={6} value={guest.password} onChange={(e)=>setGuest({...guest, password: e.target.value})} placeholder="At least 6 characters" className="input-field" data-testid="guest-password"/>
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium block mb-1.5">Preferred date</label>
                <input type="date" required min={minDate} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="input-field" data-testid="booking-date"/>
                <p className="text-xs text-black/50 mt-1">Must be after today.</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Body placement</label>
                <input required value={form.body_placement} onChange={(e) => setForm({ ...form, body_placement: e.target.value })} placeholder="e.g. Left forearm" className="input-field" data-testid="booking-placement"/>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Reference image (PNG or JPEG)</label>
                <div className="flex items-center gap-3">
                  <UploadButton
                    onUploaded={(url) => setForm({ ...form, reference_image_url: url })}
                    label={form.reference_image_url ? "Replace image" : "Upload reference"}
                    testId="booking-ref-upload"
                    accept="image/png,image/jpeg"
                  />
                  {form.reference_image_url && (
                    <div className="flex items-center gap-2">
                      <img src={form.reference_image_url} alt="reference preview" className="h-12 w-12 object-cover rounded-[6px] border-[0.5px] border-black/10" data-testid="booking-ref-preview"/>
                      <button type="button" onClick={() => setForm({ ...form, reference_image_url: "" })} className="text-xs text-black/60 underline" data-testid="booking-ref-clear">Remove</button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-black/50 mt-1">PNG or JPEG only.</p>
              </div>
              <div>
                <label className="text-sm font-medium block mb-1.5">Describe your idea</label>
                <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field" data-testid="booking-desc"/>
              </div>
              {submitErr && <div className="text-sm text-red-700" data-testid="booking-error">{submitErr}</div>}
              <div className="flex gap-2">
                <button type="submit" className="btn-filled flex-1" disabled={submitting} data-testid="booking-submit">
                  {submitting ? "Sending..." : (user ? "Send request" : "Create account & send request")}
                </button>
                <button type="button" className="btn-outline" onClick={() => setShowBook(false)} data-testid="booking-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
